package org.maplibre.compose.map

import androidx.compose.runtime.Stable
import kotlinx.serialization.json.JsonElement
import org.maplibre.compose.layers.LayerHandle
import org.maplibre.compose.sources.CustomGeometrySource
import org.maplibre.compose.sources.CustomGeometrySourceHandle
import org.maplibre.compose.sources.CustomVectorTileSource
import org.maplibre.compose.sources.CustomVectorTileSourceHandle
import org.maplibre.compose.sources.GeoJsonSource
import org.maplibre.compose.sources.GeoJsonSourceHandle
import org.maplibre.compose.sources.ImageSource
import org.maplibre.compose.sources.ImageSourceHandle
import org.maplibre.compose.sources.MutableCustomGeometrySourceHandle
import org.maplibre.compose.sources.MutableCustomVectorTileSourceHandle
import org.maplibre.compose.sources.MutableGeoJsonSourceHandle
import org.maplibre.compose.sources.MutableImageSourceHandle
import org.maplibre.compose.sources.MutableRasterDemTileSourceHandle
import org.maplibre.compose.sources.MutableRasterTileSourceHandle
import org.maplibre.compose.sources.MutableSourceHandle
import org.maplibre.compose.sources.MutableVectorTileSourceHandle
import org.maplibre.compose.sources.RasterDemTileSource
import org.maplibre.compose.sources.RasterDemTileSourceHandle
import org.maplibre.compose.sources.RasterTileSource
import org.maplibre.compose.sources.RasterTileSourceHandle
import org.maplibre.compose.sources.Source
import org.maplibre.compose.sources.SourceHandle
import org.maplibre.compose.sources.VectorTileSource
import org.maplibre.compose.sources.VectorTileSourceHandle
import org.maplibre.compose.style.Light
import org.maplibre.compose.style.Projection
import org.maplibre.compose.style.Sky
import org.maplibre.compose.style.StyleHandleException
import org.maplibre.compose.style.TransitionOptions

/** Provides lookup, iteration, and structural commands for the current loaded sources. */
@Stable
public class StyleSources internal constructor(private val style: MapStyleState) :
  Iterable<SourceHandle> {
  /**
   * Returns the current generation's handle for [id], or null when unavailable or absent. A source
   * whose style-spec type has no handle interface in this version, such as a GL JS video or canvas
   * source, has a handle that implements only [SourceHandle].
   */
  public operator fun get(id: String): SourceHandle? = style.sourceHandle(id)

  /**
   * Returns the current handle with [source]'s ID, or null while absent or no style is ready. A
   * remembered source is installed only while a declared layer references it. Look up a new handle
   * after a base-style reload or source replacement.
   */
  public operator fun get(source: Source): SourceHandle? = get(source.id)

  /** Returns the current handle with [source]'s ID and type, or null while unavailable. */
  public operator fun get(source: GeoJsonSource): GeoJsonSourceHandle? =
    get(source.id) as? GeoJsonSourceHandle

  /** Returns the current handle with [source]'s ID and type, or null while unavailable. */
  public operator fun get(source: ImageSource): ImageSourceHandle? =
    get(source.id) as? ImageSourceHandle

  /** Returns the current handle with [source]'s ID and type, or null while unavailable. */
  public operator fun get(source: VectorTileSource): VectorTileSourceHandle? =
    get(source.id) as? VectorTileSourceHandle

  /** Returns the current handle with [source]'s ID and type, or null while unavailable. */
  public operator fun get(source: RasterTileSource): RasterTileSourceHandle? =
    get(source.id) as? RasterTileSourceHandle

  /** Returns the current handle with [source]'s ID and type, or null while unavailable. */
  public operator fun get(source: RasterDemTileSource): RasterDemTileSourceHandle? =
    get(source.id) as? RasterDemTileSourceHandle

  /** Returns the current handle with [source]'s ID and type, or null while unavailable. */
  public operator fun get(source: CustomGeometrySource): CustomGeometrySourceHandle? =
    get(source.id) as? CustomGeometrySourceHandle

  /** Returns the current handle with [source]'s ID and type, or null while unavailable. */
  public operator fun get(source: CustomVectorTileSource): CustomVectorTileSourceHandle? =
    get(source.id) as? CustomVectorTileSourceHandle

  /**
   * Adds [source] to the loaded style and returns its handle once the map has installed it. The
   * command runs after the resource commands enqueued before this call, and this call suspends only
   * until the engine has run it, not until a frame has rendered.
   *
   * @return the handle, or null if no style is ready or the loaded style changes before the source
   *   is added. Nothing is added to the new style, and a warning is logged.
   * @throws IllegalStateException if the style content declares [source]'s ID, the ID is already in
   *   the style, or the map state is closed.
   * @throws StyleHandleException if the engine rejects the definition.
   */
  public suspend fun add(source: Source): MutableSourceHandle? =
    style.owner.resourceCommands.add(source)

  /** [add] for a GeoJSON source. */
  public suspend fun add(source: GeoJsonSource): MutableGeoJsonSourceHandle? =
    add(source as Source) as MutableGeoJsonSourceHandle?

  /** [add] for an image source. */
  public suspend fun add(source: ImageSource): MutableImageSourceHandle? =
    add(source as Source) as MutableImageSourceHandle?

  /** [add] for a vector tile source. */
  public suspend fun add(source: VectorTileSource): MutableVectorTileSourceHandle? =
    add(source as Source) as MutableVectorTileSourceHandle?

  /** [add] for a raster tile source. */
  public suspend fun add(source: RasterTileSource): MutableRasterTileSourceHandle? =
    add(source as Source) as MutableRasterTileSourceHandle?

  /** [add] for a raster DEM tile source. */
  public suspend fun add(source: RasterDemTileSource): MutableRasterDemTileSourceHandle? =
    add(source as Source) as MutableRasterDemTileSourceHandle?

  /** [add] for a custom geometry source. */
  public suspend fun add(source: CustomGeometrySource): MutableCustomGeometrySourceHandle? =
    add(source as Source) as MutableCustomGeometrySourceHandle?

  /** [add] for a custom vector tile source. */
  public suspend fun add(source: CustomVectorTileSource): MutableCustomVectorTileSourceHandle? =
    add(source as Source) as MutableCustomVectorTileSourceHandle?

  /** Iterates over the current loaded sources in engine style order. */
  override fun iterator(): Iterator<SourceHandle> = style.sourceHandles().values.iterator()
}

/** Provides lookup and iteration for the current loaded layers. */
@Stable
public class StyleLayers internal constructor(private val style: MapStyleState) :
  Iterable<LayerHandle> {
  /** Returns the current generation's handle for [id], or null when unavailable or absent. */
  public operator fun get(id: String): LayerHandle? = style.layerHandle(id)

  /** Iterates over the current loaded layers from bottom to top. */
  override fun iterator(): Iterator<LayerHandle> = style.layerHandles().values.iterator()
}

/**
 * Provides structural commands for style images in the current loaded-style generation.
 *
 * Writes do nothing and log a warning if no style is ready. They throw [IllegalStateException] if
 * composition declares the image ID or the map state is closed.
 */
@Stable
public class StyleImages internal constructor(private val style: MapStyleState) {
  /**
   * Enqueues [image], replacing [id] in place. Preparation owns the pixels before submission. A
   * successful replacement expires old handles; a rejected write keeps the previous image. Native
   * rejections are logged. Images declared by style content cannot be overwritten. A newer command
   * for [id] supersedes this one if it is still pending.
   */
  public fun set(id: String, image: ResolvedStyleImage) {
    setAll(mapOf(id to image))
  }

  /** Enqueues one ordered batch. Each image succeeds or fails independently. */
  public fun setAll(images: Map<String, ResolvedStyleImage>) {
    style.owner.resourceCommands.set(images)
  }

  /** Enqueues removal of [id] in the current loaded style, if it exists. */
  public fun remove(id: String) {
    style.owner.resourceCommands.removeImage(id)
  }

  /** Queries the engine after earlier resource commands; null means absent or unavailable. */
  public suspend operator fun get(id: String): StyleImageHandle? =
    style.owner.resourceCommands.image(id)
}

/**
 * Provides the global transition of the current loaded-style generation.
 *
 * A base-style reload replaces the transition with the one that the new style declares. A still
 * snapshot ignores the duration and delay.
 *
 * On Android, the system animator duration scale multiplies the transition that [set] writes. A
 * transition that the style JSON declares keeps its timing.
 */
@Stable
public class StyleTransition internal constructor(private val style: MapStyleState) {
  /**
   * Returns the loaded style's transition, or null while no style is ready.
   *
   * The reported timing is the engine's: a transition that [set] wrote is under the animator
   * duration scale of the time it was written.
   */
  public suspend fun get(): TransitionOptions? = style.transitionOptions()

  /**
   * Replaces the loaded style's transition. Does nothing and logs a warning while no style is
   * ready.
   */
  public fun set(options: TransitionOptions) {
    style.setTransitionOptions(options)
  }

  /**
   * Returns whether symbol placement changes cross-fade, or null while no style is ready.
   *
   * The cross-fade is engine behavior outside the style spec. MapLibre GL JS always reports true.
   */
  public suspend fun placementTransitions(): Boolean? = style.placementTransitions()

  /**
   * Sets whether symbol placement changes cross-fade. A cleared cross-fade applies placement
   * changes to the next rendered frame, which suits features that move at pointer frequency.
   *
   * MapLibre GL JS logs a warning and keeps the cross-fade. Does nothing and logs a warning while
   * no style is ready.
   */
  public fun setPlacementTransitions(enabled: Boolean) {
    style.setPlacementTransitions(enabled)
  }
}

/**
 * Provides the light of the current loaded-style generation.
 *
 * A base-style reload replaces the light with the one that the new style declares.
 *
 * [set] does not wait for the engine to apply the light. A light the engine rejects is logged, and
 * the style keeps its previous light.
 */
@Stable
public class StyleLight internal constructor(private val style: MapStyleState) {
  /**
   * Returns the value of the style spec's light property [name], such as `anchor` or `color`, or
   * null when the style sets no value or no style is ready.
   */
  public suspend fun getProperty(name: String): JsonElement? = style.lightProperty(name)

  /** Replaces the loaded style's light. Does nothing and logs a warning while no style is ready. */
  public fun set(light: Light) {
    style.setLight(light)
  }
}

/**
 * Provides the sky of the current loaded-style generation.
 *
 * A base-style reload replaces the sky with the one that the new style declares. MapLibre Native
 * does not support the sky: every property reads null, and a write logs a warning.
 *
 * [set] does not wait for the engine to apply the sky. A sky the engine rejects is logged, and the
 * style keeps its previous sky.
 */
@Stable
public class StyleSky internal constructor(private val style: MapStyleState) {
  /**
   * Returns the value of the style spec's sky property [name], such as `sky-color`, or null when
   * the style sets no value or no style is ready.
   */
  public suspend fun getProperty(name: String): JsonElement? = style.skyProperty(name)

  /**
   * Replaces the loaded style's sky, or removes it when [sky] is null. Does nothing and logs a
   * warning while no style is ready.
   */
  public fun set(sky: Sky?) {
    style.setSky(sky)
  }
}

/**
 * Provides the projection of the current loaded-style generation.
 *
 * A base-style reload replaces the projection with the one that the new style declares. MapLibre
 * Native supports only the Mercator projection: every property reads null, and a write logs a
 * warning.
 *
 * [set] does not wait for the engine to apply the projection. A projection the engine rejects is
 * logged, and the style keeps its previous projection.
 */
@Stable
public class StyleProjection internal constructor(private val style: MapStyleState) {
  /**
   * Returns the value of the style spec's projection property [name], which is `type`, or null when
   * the style sets no value or no style is ready.
   */
  public suspend fun getProperty(name: String): JsonElement? = style.projectionProperty(name)

  /**
   * Replaces the loaded style's projection. Does nothing and logs a warning while no style is
   * ready.
   */
  public fun set(projection: Projection) {
    style.setProjection(projection)
  }
}
